import {
  ActionButton,
  Paginator,
  Show,
  TableComponent,
  YearHeader,
} from "components";
import { useCancelledOffers } from "hooks/data/use-offers";
import { FC, useMemo } from "react";
import { cancelledOffersColumns } from "./columns";
import { withPermissions } from "utils";
import { useUpdateBubble } from "hooks/data/use-update-bubble";
import { Action } from "components/buttons/action-button";
import { useAppNavigate, useAppSearch } from "_graphql/cache/auth";
import { usePagination } from "hooks";
import SendMessageContainer from "pages/place-it-offers/message-thread";
import ViewOfferSlipContainer from "./view-slip";
import modal from "components/layouts/modal";
import FilterMenu from "./filter";
import UpdateOfferContainer from "./update";
import ViewOfferCancellationReasonContainer from "./reason";

type Props = {};

const CancelledOffers: FC<Props> = () => {
  const { updateBubble } = useUpdateBubble();
  const { offset, limit, setPage } = usePagination();

  const searchParams = useAppSearch();
  const navigate = useAppNavigate();

  const filter = useMemo(
    () => ({
      args: {
        status: ["PENDING", "OPEN", "CLOSED"],
        insurer_id: (searchParams.insurer as string[]) || [],
        payment_status: [],
        // approval_status: status_type.UNAPPROVED,
        year: searchParams.year?.toString() || "",
        claim_status: [],
        offer_from: searchParams.offer_from || "",
        class_of_business_id: (searchParams?.business as string[]) || [],
        expiry_status: searchParams?.expiry_status || undefined,
        staff_id: (searchParams?.employee as string[]) || [],
        offset,
        limit,
        search: searchParams.search || "",
      },
    }),
    [
      limit,
      offset,
      searchParams.page,
      searchParams.pageSize,
      searchParams.search,
      searchParams.year,
      searchParams?.business,
      searchParams?.employee,
      searchParams.insurer,
      searchParams.offer_from,
      searchParams?.expiry_status,
    ]
  );

  const { offers, count, refetch, loading } = useCancelledOffers({
    args: { ...filter.args },
  });

  const dispatchAction =
    (id: string, action: Exclude<Action, "expand" | "goto" | "clone">) =>
    () => {
      navigate({
        search: (old) => ({
          ...old,
          id,
          modal: action,
        }),
      });
    };

  const updateThreadBubble = (ids: string[]) => {
    updateBubble({
      variables: {
        ids,
      },
    })
      .then(() => {})
      .catch(() => {});
  };

  return (
    <main className="flex-1 flex flex-col overflow-hidden bg-background-500">
      <YearHeader />
      {/* Content goes here */}
      <div className="flex flex-1 overflow-y-auto">
        <div className="flex-1 mx-auto min-w-0  py-4 sm:py-6 overflow-hidden overflow-y-auto light flex">
          <TableComponent
            loading={loading}
            data={offers}
            title={"Cancelled Offers"}
            hasSearch
            renderFilter={({ filterOpen, setFilterOpen }) => (
              <FilterMenu open={filterOpen} setOpen={setFilterOpen} />
            )}
            columns={cancelledOffersColumns.concat({
              name: "Actions",
              accessor: "actions",
              render: (value) => (
                <>
                  <ActionButton
                    action="view"
                    tooltip="View Docs & History"
                    onClick={dispatchAction(
                      value.offer_id +
                        ":" +
                        value?.offer_from +
                        ":" +
                        value?.place_it_id,
                      "resolve"
                    )}
                  />
                  <Show if={Boolean(value.offer_from !== "INTERNAL")}>
                    <ActionButton
                      action="chat"
                      tooltip="View Message Thread"
                      onClick={dispatchAction(value?.place_it_id || "", "chat")}
                      count={
                        value.thread_notification?.filter(
                          (item) => item?.status !== "READ"
                        ).length
                      }
                      callBack={() =>
                        updateThreadBubble(
                          value.thread_notification?.map(
                            (item) => item?.id || ""
                          ) || []
                        )
                      }
                    />
                  </Show>
                  <Show
                    if={
                      value?.offer_from === "INTERNAL" ||
                      (value?.offer_from !== "INTERNAL" &&
                        value?.version === "V1") ||
                      Boolean(
                        value?.classofbusiness?.business_name
                          ?.toLowerCase()
                          ?.includes("motor")
                      )
                    }
                  >
                    {withPermissions([
                      "*:*",
                      "offers:*",
                      "offers:manage",
                      "offers:update",
                    ])(
                      <ActionButton
                        action="update"
                        tooltip="Edit Offer"
                        onClick={dispatchAction(value.offer_id, "update")}
                      />
                    )}
                  </Show>
                  <ActionButton
                    action="investigate"
                    tooltip="View Offer Cancellation Reason"
                    onClick={dispatchAction(
                      value?.offer_id || "",
                      "investigate"
                    )}
                  />
                </>
              ),
            })}
            refetch={refetch}
            pagination={() => (
              <Paginator
                offset={offset}
                limit={limit}
                totalItems={count}
                currentSize={offers.length}
                setOffset={setPage}
              />
            )}
          />
        </div>
      </div>

      {searchParams?.id && searchParams?.modal === "chat" && (
        <SendMessageContainer
          open={searchParams?.modal === "chat"}
          setOpen={(val: boolean) =>
            navigate({
              search: (old) => ({
                ...old,
                modal: val ? "chat" : undefined,
                id: val ? old?.id : undefined,
              }),
            })
          }
        />
      )}

      {searchParams?.id && searchParams?.modal === "resolve" && (
        <ViewOfferSlipContainer
          open={searchParams?.modal === "resolve"}
          setOpen={(val: boolean) =>
            navigate({
              search: (old) => ({
                ...old,
                modal: val ? "resolve" : undefined,
                id: val ? old?.id : undefined,
              }),
            })
          }
        />
      )}

      {searchParams?.id && searchParams?.modal === "update" && (
        <UpdateOfferContainer
          open={searchParams?.modal === "update"}
          setOpen={(val: boolean) =>
            navigate({
              search: (old) => ({
                ...old,
                modal: val ? "update" : undefined,
                id: val ? old?.id : undefined,
              }),
            })
          }
          refetch={refetch}
        />
      )}
      {searchParams?.id && searchParams?.modal === "investigate" && (
        <ViewOfferCancellationReasonContainer
          open={searchParams?.modal === "investigate"}
          setOpen={(val: boolean) =>
            navigate({
              search: (old) => ({
                ...old,
                modal: val ? "investigate" : undefined,
                id: val ? old?.id : undefined,
              }),
            })
          }
        />
      )}
    </main>
  );
};

export default CancelledOffers;
